import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from './ui/alert-dialog';
import { Plus, Pencil, Trash2, Loader2, ChevronLeft, ChevronRight, CalendarOff } from 'lucide-react';
import { toast } from 'sonner';

const MOTIF_COLORS = {
  "Congés payés": { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', dot: 'bg-blue-500' },
  "Maladie": { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', dot: 'bg-red-500' },
  "Sans solde": { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300', dot: 'bg-amber-500' },
  "Famille": { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-300', dot: 'bg-pink-500' },
  "Formation": { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300', dot: 'bg-purple-500' },
  "Autre": { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300', dot: 'bg-gray-500' },
};

const MONTHS = ['Janv.', 'Févr.', 'Mars', 'Avr.', 'Mai', 'Juin', 'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'];
const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function dateToIso(d) { return d.toISOString().slice(0, 10); }
function parseIso(s) { return new Date(s + 'T00:00:00'); }

function buildMonthGrid(year, month) {
  // month: 1-12. Returns array of weeks; each week is 7 dates.
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const firstWd = (firstDay.getDay() + 6) % 7; // 0=lundi
  const grid = [];
  let current = new Date(firstDay);
  current.setDate(current.getDate() - firstWd);
  while (current <= lastDay || (grid.length * 7 + grid[grid.length - 1]?.length) % 7 !== 0) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    grid.push(week);
    if (current > lastDay && (current.getDay() + 6) % 7 === 0) break;
  }
  return grid;
}

function getTodayIso() { return new Date().toISOString().slice(0, 10); }

export function AbsencesTab({ employes = [], api, isAdmin = true }) {
  const todayIso = getTodayIso();
  const initialDate = new Date();
  const [year, setYear] = useState(initialDate.getFullYear());
  const [month, setMonth] = useState(initialDate.getMonth() + 1);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filterEmp, setFilterEmp] = useState('all');
  const [filterMotif, setFilterMotif] = useState('all');

  const [form, setForm] = useState({
    employe_id: '', date_debut: todayIso, date_fin: todayIso,
    motif: 'Congés payés', motif_autre: '', notes: '',
    type_jour: 'complete',
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/rh/absences');
      setAbsences(res.data);
    } catch { toast.error('Erreur chargement absences'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    if (!form.employe_id) { toast.error('Sélectionnez un employé'); return; }
    if (form.date_fin < form.date_debut) { toast.error('Date fin avant date début'); return; }
    try {
      if (editItem) {
        await api.put(`/rh/absences/${editItem.id}`, form);
        toast.success('Absence mise à jour');
      } else {
        await api.post('/rh/absences', form);
        toast.success('Absence enregistrée');
      }
      setShowDialog(false);
      fetchAll();
    } catch (e) { toast.error('Erreur : ' + (e.response?.data?.detail || e.message)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/rh/absences/${id}`); toast.success('Supprimée'); fetchAll(); }
    catch { toast.error('Erreur suppression'); }
  };

  const openCreate = (defaultDate) => {
    setEditItem(null);
    setForm({
      employe_id: employes[0]?.id || '',
      date_debut: defaultDate || todayIso,
      date_fin: defaultDate || todayIso,
      motif: 'Congés payés',
      motif_autre: '',
      notes: '',
      type_jour: 'complete',
    });
    setShowDialog(true);
  };

  const openEdit = (a) => {
    setEditItem(a);
    setForm({
      employe_id: a.employe_id,
      date_debut: a.date_debut, date_fin: a.date_fin,
      motif: a.motif, motif_autre: a.motif_autre || '', notes: a.notes || '',
      type_jour: a.type_jour || 'complete',
    });
    setShowDialog(true);
  };

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  // Map of "YYYY-MM-DD" -> [absence...] occurring that day
  const absencesByDate = useMemo(() => {
    const map = {};
    absences.forEach(a => {
      if (filterEmp !== 'all' && a.employe_id !== filterEmp) return;
      if (filterMotif !== 'all' && a.motif !== filterMotif) return;
      let d = parseIso(a.date_debut);
      const end = parseIso(a.date_fin);
      while (d <= end) {
        const k = dateToIso(d);
        if (!map[k]) map[k] = [];
        map[k].push(a);
        d = new Date(d); d.setDate(d.getDate() + 1);
      }
    });
    return map;
  }, [absences, filterEmp, filterMotif]);

  const grid = useMemo(() => buildMonthGrid(year, month), [year, month]);

  const filteredList = useMemo(() => {
    return absences
      .filter(a => filterEmp === 'all' || a.employe_id === filterEmp)
      .filter(a => filterMotif === 'all' || a.motif === filterMotif);
  }, [absences, filterEmp, filterMotif]);

  return (
    <div className="space-y-4" data-testid="absences-tab">
      {/* Toolbar */}
      <Card>
        <CardContent className="pt-4 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
            <div className="text-lg font-semibold w-40 text-center tabular-nums">{MONTHS[month - 1]} {year}</div>
            <Button variant="ghost" size="icon" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => { const d = new Date(); setYear(d.getFullYear()); setMonth(d.getMonth() + 1); }}>Auj.</Button>
          </div>

          <div>
            <Label className="text-xs">Employé</Label>
            <Select value={filterEmp} onValueChange={setFilterEmp}>
              <SelectTrigger className="w-48" data-testid="filter-employe"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {employes.map(e => <SelectItem key={e.id} value={e.id}>{e.prenom} {e.nom}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Motif</Label>
            <Select value={filterMotif} onValueChange={setFilterMotif}>
              <SelectTrigger className="w-40" data-testid="filter-motif"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {Object.keys(MOTIF_COLORS).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {isAdmin && (
            <Button onClick={() => openCreate()} className="ml-auto" data-testid="btn-new-absence">
              <Plus className="w-4 h-4 mr-1" /> Nouvelle absence
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs">
        {Object.entries(MOTIF_COLORS).map(([m, c]) => (
          <div key={m} className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded-full ${c.dot}`} /><span>{m}</span></div>
        ))}
      </div>

      {/* Calendar */}
      <Card>
        <CardContent className="p-3">
          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <div>
              <div className="grid grid-cols-7 gap-1 text-xs font-semibold text-center text-muted-foreground mb-1">
                {DAYS.map(d => <div key={d} className="py-1">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {grid.flat().map((d, i) => {
                  const inMonth = d.getMonth() + 1 === month;
                  const key = dateToIso(d);
                  const isToday = key === todayIso;
                  const dayAbs = absencesByDate[key] || [];
                  return (
                    <button
                      key={i}
                      onClick={() => isAdmin && openCreate(key)}
                      className={`min-h-[80px] p-1.5 rounded border text-left transition ${
                        inMonth ? 'bg-background' : 'bg-muted/30 opacity-50'
                      } ${isToday ? 'border-primary border-2' : 'border-border'} ${isAdmin ? 'hover:bg-accent/40 cursor-pointer' : 'cursor-default'}`}
                      data-testid={`day-${key}`}
                    >
                      <div className="text-xs font-semibold mb-0.5">{d.getDate()}</div>
                      <div className="space-y-0.5">
                        {dayAbs.slice(0, 3).map((a, j) => {
                          const c = MOTIF_COLORS[a.motif] || MOTIF_COLORS["Autre"];
                          const halfLabel = a.type_jour === 'demi_matin' ? ' ½AM' : a.type_jour === 'demi_apres_midi' ? ' ½PM' : '';
                          return (
                            <div
                              key={j}
                              onClick={(e) => { e.stopPropagation(); openEdit(a); }}
                              className={`text-[10px] leading-tight px-1 py-0.5 rounded ${c.bg} ${c.text} truncate`}
                              title={`${a.employe_nom} - ${a.motif}${halfLabel ? ' (' + halfLabel.trim() + ')' : ''}`}
                            >
                              {a.employe_nom.split(' ')[0]} · {a.motif}{halfLabel}
                            </div>
                          );
                        })}
                        {dayAbs.length > 3 && <div className="text-[10px] text-muted-foreground">+{dayAbs.length - 3}</div>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CalendarOff className="w-4 h-4" /> Liste détaillée ({filteredList.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employé</TableHead>
                <TableHead>Motif</TableHead>
                <TableHead>Du</TableHead>
                <TableHead>Au</TableHead>
                <TableHead className="text-right">Jours</TableHead>
                <TableHead>Notes</TableHead>
                {isAdmin && <TableHead className="w-24"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredList.length === 0 ? (
                <TableRow><TableCell colSpan={isAdmin ? 7 : 6} className="text-center text-muted-foreground py-6">Aucune absence enregistrée</TableCell></TableRow>
              ) : filteredList.map(a => {
                const c = MOTIF_COLORS[a.motif] || MOTIF_COLORS["Autre"];
                return (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">{a.employe_nom}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${c.bg} ${c.text} ${c.border} text-xs`}>{a.motif}</Badge>
                      {a.motif === 'Autre' && a.motif_autre && <span className="ml-2 text-xs text-muted-foreground">({a.motif_autre})</span>}
                    </TableCell>
                    <TableCell className="text-xs tabular-nums">{a.date_debut}</TableCell>
                    <TableCell className="text-xs tabular-nums">{a.date_fin}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      <Badge variant="secondary">{a.nb_jours}j</Badge>
                      {a.type_jour && a.type_jour !== 'complete' && (
                        <Badge variant="outline" className="ml-1 text-[9px] h-4 px-1">
                          {a.type_jour === 'demi_matin' ? '½ matin' : '½ AM'}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{a.notes}</TableCell>
                    {isAdmin && (
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(a)} data-testid={`edit-${a.id}`}><Pencil className="w-3 h-3" /></Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"><Trash2 className="w-3 h-3" /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Supprimer cette absence ?</AlertDialogTitle><AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => remove(a.id)}>Supprimer</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog création/édition */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md" data-testid="dialog-absence">
          <DialogHeader><DialogTitle>{editItem ? 'Modifier' : 'Nouvelle'} absence</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Employé</Label>
              <Select value={form.employe_id} onValueChange={v => setForm(p => ({ ...p, employe_id: v }))}>
                <SelectTrigger data-testid="form-employe"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                <SelectContent>{employes.map(e => <SelectItem key={e.id} value={e.id}>{e.prenom} {e.nom}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Du</Label>
                <Input type="date" value={form.date_debut} onChange={e => setForm(p => ({ ...p, date_debut: e.target.value }))} data-testid="form-date-debut" />
              </div>
              <div>
                <Label>Au</Label>
                <Input type="date" value={form.date_fin} min={form.date_debut} onChange={e => setForm(p => ({ ...p, date_fin: e.target.value }))} data-testid="form-date-fin" />
              </div>
            </div>
            <div>
              <Label>Durée</Label>
              <Select value={form.type_jour} onValueChange={v => setForm(p => ({ ...p, type_jour: v }))}>
                <SelectTrigger data-testid="form-type-jour"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="complete">Journée complète</SelectItem>
                  <SelectItem value="demi_matin">Demi-journée — matin</SelectItem>
                  <SelectItem value="demi_apres_midi">Demi-journée — après-midi</SelectItem>
                </SelectContent>
              </Select>
              {form.type_jour !== 'complete' && form.date_debut !== form.date_fin && (
                <p className="text-[11px] text-amber-600 mt-1">⚠️ La demi-journée ne s&apos;applique que pour une absence d&apos;un seul jour. Pour une plage, choisissez Journée complète.</p>
              )}
            </div>
            <div>
              <Label>Motif</Label>
              <Select value={form.motif} onValueChange={v => setForm(p => ({ ...p, motif: v }))}>
                <SelectTrigger data-testid="form-motif"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(MOTIF_COLORS).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.motif === 'Autre' && (
              <div>
                <Label>Préciser le motif</Label>
                <Input value={form.motif_autre} onChange={e => setForm(p => ({ ...p, motif_autre: e.target.value }))} placeholder="ex: convocation tribunal" data-testid="form-motif-autre" />
              </div>
            )}
            <div>
              <Label>Notes (facultatif)</Label>
              <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} data-testid="form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
            <Button onClick={save} data-testid="form-save">{editItem ? 'Modifier' : 'Créer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
